import React, { useState, useEffect } from "react";
import JSZip from "jszip";
import { api, colones, fmtFecha, initials } from "@/lib/api";
import { X, User as UserIcon, Briefcase, Calculator, Check, AlertCircle, MapPin, Phone, Mail, FolderOpen, FileText, FileSignature, Trash2, Download, CheckCircle2, Clock as ClockIcon, Circle, Package, Loader2, UserCog, FileSpreadsheet, Palmtree } from "lucide-react";
import { toast } from "sonner";
import { exportarEmpleadoExcel, exportarEmpleadoPDF } from "@/lib/employeeExport";
import { calcularPeriodosDetallados } from "@/lib/vacaciones";
import HistorialCampoEmpleado from "@/components/HistorialCampoEmpleado";

const TABS = [
  { id: "personal", label: "Personal", icon: UserIcon },
  { id: "laboral", label: "Laboral", icon: Briefcase },
  { id: "desglose", label: "Desglose Salarial", icon: Calculator },
  { id: "archivo", label: "Archivo", icon: FolderOpen },
];

const Field = ({ label, value, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-xs uppercase tracking-widest text-secondary-c font-medium mb-1.5">{label}</div>
    <div className="font-display text-base font-medium text-primary-c whitespace-pre-wrap break-words">{value || <span className="text-secondary-c opacity-60 italic">No registrado</span>}</div>
  </div>
);

const ESTADO_CIVIL_FMT = (v) => v || "—";

export default function EmployeeDetailModal({ empleado, empresa = {}, onClose, onUpdated }) {
  const [tab, setTab] = useState("personal");
  const [e, setE] = useState(empleado);
  useEffect(() => { setE(empleado); }, [empleado]);
  if (!empleado || !e) return null;
  const salario = Number(e.salario_base) || 0;

  // Períodos de pago
  const mensual = salario;
  const quincenal = salario / 2;
  const semanal = salario / 4.33;

  // "Días base" para calcular valor del día según Actividad Económica de la empresa
  // + Frecuencia de pago del empleado (ver /lib/salaryBasis.js)
  const grupoAct = empresa?.actividad_grupo;
  const freq = String(e.frecuencia_pago || "Mensual").toLowerCase();
  const diasBase = (grupoAct === "servicios_industria" && freq === "semanal") ? 26 : 30;
  const diario = salario / diasBase;

  // Horas por jornada (CR labor law: Diurna 8h, Mixta 7h, Nocturna 6h por día)
  const horaDiurna = diario / 8;
  const horaMixta = diario / 7;
  const horaNocturna = diario / 6;

  const FRECUENCIA_KEY = (e.frecuencia_pago || "Mensual").toLowerCase();
  const JORNADA = e.jornada || "Diurna";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl fade-in-up" onClick={(ev) => ev.stopPropagation()} data-testid="employee-detail-modal">

        {/* Header */}
        <div className="p-6 border-b border-c flex items-start justify-between gap-3">
          <h3 className="font-display text-2xl font-semibold">Detalle del Empleado</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const { data: ast } = await api.get("/asistencia").catch(() => ({ data: [] }));
                  const { saldoTotal } = calcularPeriodosDetallados(e, ast || [], new Date(), empresa);
                  exportarEmpleadoExcel({ ...e, __vacaciones_saldo: saldoTotal }, empresa);
                  toast.success("Excel generado");
                } catch (err) { toast.error("No se pudo exportar Excel: " + err.message); }
              }}
              className="np-btn np-btn-outline text-xs py-1.5 px-3"
              title="Exportar toda la información del empleado a Excel"
              data-testid="detail-export-excel-btn"
            >
              <FileSpreadsheet size={14} className="mr-1.5" /> Excel
            </button>
            <button
              onClick={async () => {
                try {
                  const { data: ast } = await api.get("/asistencia").catch(() => ({ data: [] }));
                  const { saldoTotal } = calcularPeriodosDetallados(e, ast || [], new Date(), empresa);
                  exportarEmpleadoPDF({ ...e, __vacaciones_saldo: saldoTotal }, empresa);
                  toast.success("PDF generado");
                } catch (err) { toast.error("No se pudo exportar PDF: " + err.message); }
              }}
              className="np-btn np-btn-outline text-xs py-1.5 px-3"
              title="Exportar toda la información del empleado a PDF"
              data-testid="detail-export-pdf-btn"
            >
              <FileText size={14} className="mr-1.5" /> PDF
            </button>
            <button onClick={onClose} className="np-btn np-btn-ghost p-2" data-testid="detail-close-btn"><X size={18} /></button>
          </div>
        </div>

        {/* Identity strip */}
        <div className="px-6 py-5 flex items-center gap-4 border-b border-c">
          {e.avatar ? (
            <img src={e.avatar} alt="" className="w-16 h-16 rounded-2xl object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center font-display text-xl font-semibold">
              {initials(`${e.nombre} ${e.apellidos || ""}`)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-display text-2xl font-semibold leading-tight truncate">{e.nombre} {e.apellidos}</div>
            <div className="text-sm text-secondary-c mt-0.5">{e.puesto}{e.departamento ? ` · ${e.departamento}` : ""}</div>
          </div>
          <span className={`np-badge ${e.estado === "Activo" ? "np-badge-success" : "np-badge-neutral"}`}>{e.estado || "—"}</span>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-5 border-b border-c">
          <div className="inline-flex w-full rounded-xl bg-[#EFEEE8] p-1">
            {TABS.map((t) => {
              const Ic = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`detail-tab-${t.id}`}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${active ? "bg-white text-primary-c shadow-sm" : "text-secondary-c hover:text-primary-c"}`}>
                  <Ic size={15} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {tab === "personal" && (
            <div className="space-y-7">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                <Field label="Cédula" value={e.cedula} />
                <Field label="Fecha de nacimiento" value={e.fecha_nacimiento ? fmtFecha(e.fecha_nacimiento) : null} />
                <Field label="Género" value={e.genero} />
                <Field label="Estado civil" value={ESTADO_CIVIL_FMT(e.estado_civil)} />
                <Field label="Teléfono" value={e.telefono} />
                <Field label="Correo electrónico" value={e.correo} />
                <Field label="Dirección" value={e.direccion} full />
              </div>
              {(e.contacto_emergencia || e.telefono_emergencia || e.parentesco_emergencia) && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle size={14} className="text-warning" /> Contacto de emergencia
                  </div>
                  <div className="p-4 rounded-xl bg-[#FFF7E5] border border-[#F0CC8C]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-secondary-c font-medium mb-0.5">Nombre</div>
                        <div className="font-medium flex items-center gap-2 text-sm"><UserIcon size={14} className="text-warning" /> {e.contacto_emergencia || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-secondary-c font-medium mb-0.5">Parentesco</div>
                        <div className="text-sm">{e.parentesco_emergencia || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-secondary-c font-medium mb-0.5">Teléfono</div>
                        <div className="text-sm flex items-center gap-2">
                          <Phone size={14} className="text-warning" />
                          {e.telefono_emergencia
                            ? <a href={`tel:${e.telefono_emergencia}`} className="hover:underline">{e.telefono_emergencia}</a>
                            : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "laboral" && (
            <div className="space-y-7">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                <Field label="Código de empleado" value={e.codigo} />
                <Field label="Departamento" value={e.departamento} />
                <div>
                  <Field label="Puesto" value={e.puesto} />
                  <HistorialCampoEmpleado empleado={e} field="puesto" onUpdated={(u) => { setE(u); onUpdated?.(u); }} />
                </div>
                <Field label="Fecha de ingreso" value={e.fecha_ingreso ? fmtFecha(e.fecha_ingreso) : null} />
                <div>
                  <Field label="Jornada" value={e.jornada} />
                  <HistorialCampoEmpleado empleado={e} field="jornada" onUpdated={(u) => { setE(u); onUpdated?.(u); }} />
                </div>
                <div>
                  <Field label="Frecuencia de pago" value={e.frecuencia_pago} />
                  <HistorialCampoEmpleado empleado={e} field="frecuencia_pago" onUpdated={(u) => { setE(u); onUpdated?.(u); }} />
                </div>
                <Field label="N.º Seguro Social" value={e.numero_seguro_social} />
                <Field label="Banco" value={e.banco} />
                <Field label="Cuenta bancaria" value={e.cuenta_bancaria} />
                <VacacionesDisponibles empleado={e} empresa={empresa} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`flex items-start gap-3 p-4 rounded-xl border ${e.ccss_aplica ? "bg-[#E6EFEA] border-[#C8DACF]" : "bg-[#F4F3EE] border-[var(--border)]"}`}>
                  <div className={`w-2 h-2 rounded-full mt-2 ${e.ccss_aplica ? "bg-success" : "bg-[#C2C5C3]"}`} />
                  <div className="flex-1">
                    <div className="font-medium text-sm flex items-center gap-2">CCSS {e.ccss_aplica ? <Check size={14} className="text-success" /> : null}{e.pensionado && <span className="np-badge np-badge-info text-[10px]">Pensionado</span>}</div>
                    <div className="text-xs text-secondary-c mt-0.5">
                      {!e.ccss_aplica
                        ? "No aplica rebajo CCSS"
                        : e.pensionado
                          ? `Aplica rebajo reducido (${(Number(empresa?.ccss_pensionado_pct) || 5.5).toFixed(2).replace(/\.?0+$/, "")} %) por ser pensionado`
                          : "Aplica rebajo del 10,83 % del salario bruto"}
                    </div>
                    {e.ccss_aplica && e.fecha_ccss && (
                      <div className="text-xs text-secondary-c mt-1">Ingreso a CCSS: <strong className="text-primary-c">{fmtFecha(e.fecha_ccss)}</strong></div>
                    )}
                    <HistorialCampoEmpleado empleado={e} field="ccss_aplica" onUpdated={(u) => { setE(u); onUpdated?.(u); }} />
                    <HistorialCampoEmpleado empleado={e} field="pensionado" onUpdated={(u) => { setE(u); onUpdated?.(u); }} />
                  </div>
                </div>
                <div className={`flex items-start gap-3 p-4 rounded-xl border ${e.almuerzo_paga ? "bg-[#E6EFEA] border-[#C8DACF]" : "bg-[#FFF4E5] border-[#F2DFB6]"}`}>
                  <div className={`w-2 h-2 rounded-full mt-2 ${e.almuerzo_paga ? "bg-success" : "bg-warning"}`} />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Hora de almuerzo</div>
                    <div className="text-xs text-secondary-c mt-0.5">{e.almuerzo_paga ? "La hora de almuerzo es paga" : "Se descuenta 1h de almuerzo"}</div>
                    <HistorialCampoEmpleado empleado={e} field="almuerzo_paga" onUpdated={(u) => { setE(u); onUpdated?.(u); }} />
                  </div>
                </div>
              </div>

              {e.fecha_ingreso && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-secondary-c font-medium mb-3">Hitos de antigüedad</div>
                  <EmployeeTimeline fechaIngreso={e.fecha_ingreso} />
                </div>
              )}

              <HistorialVacaciones empleadoId={e.id} />

              {e.notas && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-secondary-c font-medium mb-2">Notas</div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap p-4 rounded-xl bg-[#FAFAF7] border border-c">{e.notas}</div>
                </div>
              )}
            </div>
          )}

          {tab === "desglose" && (
            <div className="space-y-8">
              {grupoAct && (
                <div className="p-3 rounded-xl border border-c bg-[#FAFAF7] text-xs text-secondary-c" data-testid="desglose-basis-hint">
                  Base de cálculo: <strong className="text-primary-c">salario ÷ {diasBase}</strong>
                  {" — "}
                  Actividad: <strong className="text-primary-c">{grupoAct === "comercio_hoteleria" ? "Comercio · Hotelería (Quincenal/Mensual)" : grupoAct === "servicios_jornadas_especiales" ? "Servicios con Jornadas especiales (Quincenal/Mensual)" : "Servicios · Industria · Agricultura · Ganadería (Semanal)"}</strong>
                  {" · "}
                  Frecuencia empleado: <strong className="text-primary-c">{e.frecuencia_pago || "Mensual"}</strong>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-3">Períodos de pago</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { k: "mensual", label: "Mensual", val: mensual },
                    { k: "quincenal", label: "Quincenal", val: quincenal },
                    { k: "semanal", label: "Semanal", val: semanal },
                    { k: "diario", label: "Diario", val: diario },
                  ].map((p) => {
                    const active = FRECUENCIA_KEY.startsWith(p.k.slice(0, 5));
                    return (
                      <div key={p.k} className={`p-4 rounded-xl border ${active ? "bg-[#E6EFEA] border-[#274735]" : "bg-[#FAFAF7] border-c"}`}>
                        <div className="text-xs text-secondary-c font-medium">{p.label}</div>
                        <div className={`font-display text-xl font-semibold mt-1 ${active ? "text-primary-color" : "text-primary-c"}`}>{colones(p.val)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-3">Horas por jornada</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5">
                  {[
                    { label: "Hora Diurna", base: horaDiurna, jKey: "Diurna", hint: "8h por día" },
                    { label: "Hora Mixta", base: horaMixta, jKey: "Mixta", hint: "7h por día" },
                    { label: "Hora Nocturna", base: horaNocturna, jKey: "Nocturna", hint: "6h por día" },
                  ].map((h) => {
                    const isRotativa = JORNADA === "Rotativa";
                    const active = JORNADA === h.jKey;
                    return (
                      <div key={h.label}>
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-secondary-c font-medium">{h.label}</div>
                          {active && <span className="np-badge np-badge-success text-[10px] py-0.5">jornada actual</span>}
                          {isRotativa && <span className="np-badge np-badge-info text-[10px] py-0.5">según turno</span>}
                        </div>
                        <div className="font-display text-2xl font-semibold mt-1">{colones(h.base)}</div>
                        <div className="text-[11px] text-secondary-c opacity-70 mt-0.5">{h.hint}</div>
                      </div>
                    );
                  })}
                  {[
                    { label: "Hora Diurna Extra (×1.5)", base: horaDiurna * 1.5 },
                    { label: "Hora Mixta Extra (×1.5)", base: horaMixta * 1.5 },
                    { label: "Hora Nocturna Extra (×1.5)", base: horaNocturna * 1.5 },
                  ].map((h) => (
                    <div key={h.label}>
                      <div className="text-xs text-secondary-c font-medium">{h.label}</div>
                      <div className="font-display text-2xl font-semibold mt-1 text-accent-color">{colones(h.base)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FAFAF7] border border-c text-xs text-secondary-c">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <div>Cálculos de referencia basados en el salario base ({colones(salario)}). En las planillas se aplican según las horas reales registradas en asistencia, con el rebajo de CCSS y los rebajos activos del empleado.</div>
              </div>
              <HistorialCampoEmpleado empleado={e} field="salario_base" onUpdated={(u) => { setE(u); onUpdated?.(u); }} />
            </div>
          )}

          {tab === "archivo" && (
            <ArchivoTab empleadoId={e.id} empleadoNombre={`${e.nombre} ${e.apellidos || ""}`.trim()} />
          )}
        </div>
      </div>
    </div>
  );
}

function EmployeeTimeline({ fechaIngreso }) {
  const ingreso = new Date(`${fechaIngreso.slice(0, 10)}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const MILESTONES = [
    { label: "3 meses",  months: 3 },
    { label: "6 meses",  months: 6 },
    { label: "1 año",    months: 12 },
    { label: "2 años",   months: 24 },
    { label: "3 años",   months: 36 },
    { label: "5 años",   months: 60 },
    { label: "10 años",  months: 120 },
  ];
  const hitos = MILESTONES.map(m => {
    const target = new Date(ingreso);
    target.setMonth(target.getMonth() + m.months);
    const dias = Math.ceil((target - today) / 86400000);
    const estado = dias < 0 ? "cumplido" : dias === 0 ? "hoy" : dias <= 30 ? "proximo" : "futuro";
    return { ...m, fecha: target.toISOString().slice(0, 10), dias, estado };
  });
  // Mostrar solo hasta 1 hito posterior al más cercano futuro (recorta 10 años si no aplica)
  const idxNext = hitos.findIndex(h => h.estado !== "cumplido");
  const corte = idxNext === -1 ? hitos.length : Math.min(hitos.length, idxNext + 2);
  const visibles = hitos.slice(0, corte);

  return (
    <div className="relative pl-6 space-y-3">
      <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[var(--border)]" aria-hidden="true" />
      {visibles.map((h) => {
        const Icon = h.estado === "cumplido" || h.estado === "hoy" ? CheckCircle2 : h.estado === "proximo" ? ClockIcon : Circle;
        const iconColor = h.estado === "cumplido" ? "text-success" : h.estado === "hoy" ? "text-primary-color" : h.estado === "proximo" ? "text-warning" : "text-secondary-c opacity-60";
        const bg = h.estado === "proximo" ? "bg-[#FFF7E5] border-[#F0CC8C]" : h.estado === "hoy" ? "bg-[#E6EFEA] border-[#C8DACF]" : "bg-white border-c";
        return (
          <div key={`hito-${h.months}`} className="relative flex items-start gap-3" data-testid={`timeline-hito-${h.months}m`}>
            <div className="absolute -left-6 top-1.5 bg-white rounded-full">
              <Icon size={16} className={iconColor} strokeWidth={2} />
            </div>
            <div className={`flex-1 flex items-center justify-between gap-3 p-2.5 rounded-lg border ${bg}`}>
              <div>
                <div className="text-sm font-medium">{h.label}</div>
                <div className="text-xs text-secondary-c">{fmtFecha(h.fecha)}</div>
              </div>
              <div className="text-right">
                {h.estado === "cumplido" ? (
                  <span className="text-xs text-success font-medium">Cumplido hace {Math.abs(h.dias)} d</span>
                ) : h.estado === "hoy" ? (
                  <span className="text-xs text-primary-color font-semibold">¡Hoy!</span>
                ) : h.estado === "proximo" ? (
                  <span className="text-xs text-warning font-semibold">Faltan {h.dias} d</span>
                ) : (
                  <span className="text-xs text-secondary-c">En {h.dias} d</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ArchivoTab({ empleadoId, empleadoNombre = "Empleado" }) {  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [tipo, setTipo] = useState("all"); // all | comprobante | accion | liquidacion | contrato
  const [zipping, setZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [cs, ds, ls, ks, hrs] = await Promise.allSettled([
        api.get(`/comprobantes?empleado_id=${empleadoId}`),
        api.get(`/acciones-documentos?empleado_id=${empleadoId}`),
        api.get(`/liquidaciones?empleado_id=${empleadoId}`),
        api.get(`/contratos?empleado_id=${empleadoId}`),
        api.get(`/acciones-personal?empleado_id=${empleadoId}`),
      ]);
      try {
        const cData = cs.status === "fulfilled" ? (cs.value.data || []) : [];
        const dData = ds.status === "fulfilled" ? (ds.value.data || []) : [];
        const lData = ls.status === "fulfilled" ? (ls.value.data || []) : [];
        const kData = ks.status === "fulfilled" ? (ks.value.data || []) : [];
        const hrData = hrs.status === "fulfilled" ? (hrs.value.data || []) : [];
        const KIND_LABELS = {
          "aguinaldo": "Aguinaldo",
          "vacaciones_pagadas": "Vacaciones Pagadas",
          "quincena": "Quincena",
          "semana": "Semana",
          "mes": "Mes",
        };
        const comprobantes = cData.map(c => ({
          ...c,
          tipo: "comprobante",
          titulo: c.periodo_label || c.file_name,
          subtitulo: KIND_LABELS[c.kind] || c.kind,
          fecha: c.fecha_inicio || c.fecha_emision,
          delete_url: `/comprobantes/${c.id}`,
          download_url: `/comprobantes/${c.id}`,
        }));
        const acciones = dData.map(d => ({
          ...d,
          tipo: "accion",
          titulo: d.nombre || d.file_name,
          subtitulo: d.template_nombre || d.categoria || "Documento",
          fecha: d.created_at,
          delete_url: `/acciones-documentos/${d.id}`,
          download_url: `/acciones-documentos/${d.id}`,
        }));
        const liquidaciones = lData.map(l => ({
          ...l,
          tipo: "liquidacion",
          titulo: l.file_name || `Liquidación ${l.fecha_salida || ""}`.trim(),
          subtitulo: l.motivo_label || "Liquidación laboral",
          fecha: l.fecha_salida || l.created_at,
          delete_url: `/liquidaciones/${l.id}`,
          download_url: `/liquidaciones/${l.id}`,
          monto_neto: l.total,
        }));
        const contratos = kData.map(k => ({
          ...k,
          tipo: "contrato",
          titulo: k.file_name || "Contrato laboral",
          subtitulo: "Contrato laboral",
          fecha: k.fecha_firma || k.created_at,
          delete_url: `/contratos/${k.id}`,
          download_url: `/contratos/${k.id}`,
        }));
        const accionesHR = hrData.map(h => {
          const orig = h.tipo;
          let titulo, subtitulo;
          if (orig === "ascenso") {
            titulo = `Ascenso a ${h.puesto_nuevo || "—"}`;
            subtitulo = `${h.puesto_anterior || "—"} → ${h.puesto_nuevo || "—"} · ${h.estado === "confirmado" ? "Confirmado" : h.estado === "revertido" ? "Revertido" : "En prueba"}`;
          } else if (orig === "cambio_puesto") {
            titulo = `Cambio de puesto: ${h.puesto_nuevo || "—"}`;
            subtitulo = `${h.puesto_anterior || "—"} → ${h.puesto_nuevo || "—"}`;
          } else if (orig === "descenso") {
            titulo = `Descenso a ${h.puesto_nuevo || "—"}`;
            subtitulo = `${h.puesto_anterior || "—"} → ${h.puesto_nuevo || "—"}`;
          } else if (orig === "renuncia") {
            titulo = "Renuncia";
            subtitulo = `Motivo: ${h.motivo || "—"}`;
          } else {
            titulo = "Despido";
            subtitulo = `Motivo: ${h.motivo || "—"}`;
          }
          return {
            ...h,
            tipo: "accion_hr",
            titulo,
            subtitulo,
            fecha: h.fecha || h.created_at,
            delete_url: `/acciones-personal/${h.id}`,
            download_url: null,
            no_download: true,
          };
        });
        const merged = [...comprobantes, ...acciones, ...liquidaciones, ...contratos, ...accionesHR].sort((a, b) =>
          (b.fecha || "").localeCompare(a.fecha || "")
        );
        if (mounted) setItems(merged);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [empleadoId]);

  const descargar = async (it) => {
    try {
      const { data } = await api.get(it.download_url);
      if (!data?.pdf_base64) return;
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${data.pdf_base64}`;
      link.download = data.file_name || `Documento_${it.id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { console.error(e); }
  };

  const eliminar = async (it) => {
    if (!window.confirm("¿Eliminar este documento del archivo?")) return;
    await api.delete(it.delete_url);
    setItems(items.filter(i => i.id !== it.id));
  };

  // Folder name por tipo + sanitize
  const carpetaPorTipo = (t) => ({
    comprobante: "Comprobantes",
    accion: "Acciones",
    liquidacion: "Liquidaciones",
    contrato: "Contratos",
  }[t] || "Otros");
  const sanitize = (s) => String(s || "doc").replace(/[^A-Za-z0-9_\-\.]+/g, "_");

  const descargarExpediente = async () => {
    if (items.length === 0) { toast.info("No hay documentos para empacar"); return; }
    setZipping(true);
    setZipProgress({ done: 0, total: items.length });
    try {
      const zip = new JSZip();
      let okCount = 0;
      let errCount = 0;
      const printable = items.filter(it => !it.no_download && it.download_url);
      setZipProgress({ done: 0, total: printable.length });
      for (let i = 0; i < printable.length; i++) {
        const it = printable[i];
        try {
          const { data } = await api.get(it.download_url);
          if (data?.pdf_base64) {
            const folder = zip.folder(carpetaPorTipo(it.tipo));
            const fname = sanitize(data.file_name || `${it.titulo || it.id}.pdf`);
            folder.file(fname, data.pdf_base64, { base64: true });
            okCount++;
          } else {
            errCount++;
          }
        } catch { errCount++; }
        setZipProgress({ done: i + 1, total: printable.length });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const safeName = sanitize(empleadoNombre.normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Expediente_${safeName}_${today}.zip`;
      document.body.appendChild(link); link.click();
      setTimeout(() => { URL.revokeObjectURL(link.href); link.remove(); }, 100);
      toast.success(`Expediente descargado: ${okCount} documentos${errCount ? ` (${errCount} fallaron)` : ""}`);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo generar el expediente");
    } finally {
      setZipping(false);
      setZipProgress({ done: 0, total: 0 });
    }
  };

  // Años disponibles para el selector
  const refDate = (it) => (it.fecha || "").slice(0, 10);
  const years = Array.from(new Set(items.map(it => refDate(it).slice(0, 4)).filter(Boolean))).sort((a, b) => b.localeCompare(a));
  const MES_NOMBRES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

  const filtered = items.filter(it => {
    if (tipo !== "all" && it.tipo !== tipo) return false;
    const f = refDate(it);
    if (!f) return year === "all" && month === "all";
    if (year !== "all" && f.slice(0, 4) !== year) return false;
    if (month !== "all" && f.slice(5, 7) !== month) return false;
    return true;
  });

  if (loading) {
    return <div className="text-sm text-secondary-c">Cargando archivo…</div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Documentos del expediente</div>
            {items.length > 0 && (
              <button
                onClick={descargarExpediente}
                disabled={zipping}
                className="np-btn np-btn-primary text-xs py-1.5 px-3 disabled:opacity-60"
                title="Empacar todos los PDF en un ZIP"
                data-testid="archivo-zip-btn"
              >
                {zipping
                  ? <><Loader2 size={12} className="mr-1.5 animate-spin" />Empacando {zipProgress.done}/{zipProgress.total}…</>
                  : <><Package size={12} className="mr-1.5" />Descargar expediente</>}
              </button>
            )}
          </div>
          {items.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={tipo}
                onChange={e => setTipo(e.target.value)}
                className="np-input text-sm py-1.5 pr-7"
                data-testid="archivo-filter-tipo"
              >
                <option value="all">Todos los tipos</option>
                <option value="comprobante">Comprobantes de pago</option>
                <option value="accion">Documentos (Cartas)</option>
                <option value="accion_hr">Ascensos / Despidos</option>
                <option value="liquidacion">Liquidaciones</option>
                <option value="contrato">Contratos</option>
              </select>
              <select
                value={year}
                onChange={e => setYear(e.target.value)}
                className="np-input text-sm py-1.5 pr-7"
                data-testid="archivo-filter-year"
              >
                <option value="all">Todos los años</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="np-input text-sm py-1.5 pr-7"
                data-testid="archivo-filter-month"
              >
                <option value="all">Todos los meses</option>
                {MES_NOMBRES.map((m, i) => <option key={m} value={String(i+1).padStart(2,"0")}>{m}</option>)}
              </select>
              <span className="text-xs text-secondary-c whitespace-nowrap" data-testid="archivo-count">{filtered.length} de {items.length}</span>
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 rounded-xl bg-[#FAFAF7] border border-dashed border-c">
            <FolderOpen size={32} className="text-secondary-c opacity-60" />
            <div className="text-sm text-secondary-c">Aún no hay documentos archivados para este empleado.</div>
            <div className="text-xs text-secondary-c opacity-70">Los comprobantes de pago y las acciones de personal se guardan aquí automáticamente.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 rounded-xl bg-[#FAFAF7] border border-dashed border-c">
            <FolderOpen size={28} className="text-secondary-c opacity-60" />
            <div className="text-sm text-secondary-c">Sin documentos para el filtro seleccionado.</div>
            <button onClick={() => { setYear("all"); setMonth("all"); setTipo("all"); }} className="text-xs text-primary-c underline" data-testid="archivo-clear-filter">Ver todos</button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="archivo-list">
            {filtered.map(it => (
              <div key={`${it.tipo}-${it.id}`} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-c bg-white hover:bg-[#FAFAF7] transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${it.tipo === "accion_hr" ? "bg-[#E6EFEA] text-primary-color" : it.tipo === "accion" ? "bg-[#FFF4E5] text-[#A67115]" : it.tipo === "contrato" ? "bg-[#E8EEF7] text-[#2D5DA9]" : "bg-[#E6EFEA] text-primary-color"}`}>
                    {it.tipo === "accion_hr" ? <UserCog size={18} strokeWidth={1.5} /> : it.tipo === "contrato" ? <FileSignature size={18} strokeWidth={1.5} /> : <FileText size={18} strokeWidth={1.5} />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{it.titulo}</div>
                    <div className="text-xs text-secondary-c flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span className="capitalize">{it.subtitulo}</span>
                      <span>Emitido {it.fecha ? fmtFecha(it.fecha.slice(0,10)) : "—"}</span>
                      {it.monto_neto != null && <span className="text-success font-medium">Neto: {colones(it.monto_neto)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!it.no_download && (
                    <button onClick={() => descargar(it)} className="np-btn np-btn-outline p-2" title="Descargar PDF" data-testid={`archivo-download-${it.id}`}>
                      <Download size={14} />
                    </button>
                  )}
                  <button onClick={() => eliminar(it)} className="np-btn np-btn-ghost p-2 text-error hover:bg-[#FDEDED]" title="Eliminar" data-testid={`archivo-delete-${it.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FAFAF7] border border-c text-xs text-secondary-c">
        <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
        <div>Aquí encontrás comprobantes de pago, acciones de personal, liquidaciones y contratos del expediente del empleado.</div>
      </div>
    </div>
  );
}


function HistorialVacaciones({ empleadoId }) {
  const [loading, setLoading] = useState(true);
  const [rangos, setRangos] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/asistencia");
        const list = (data || [])
          .filter(a => a.empleado_id === empleadoId && a.estado === "Vacaciones")
          .sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
        // Pagadas: un registro = un grupo (dias = dias_pagados). Disfrutadas: contiguo agrupado.
        const grupos = [];
        for (const r of list) {
          const tipo = r.tipo_vacacion || "Disfrutadas";
          if (tipo === "Pagadas") {
            const dias = Number(r.dias_pagados) > 0 ? Number(r.dias_pagados) : 1;
            grupos.push({ fecha_inicio: r.fecha, fecha_fin: r.fecha, dias, tipo, monto_pagado: Number(r.monto_pagado) || 0 });
            continue;
          }
          const prev = grupos[grupos.length - 1];
          if (prev && prev.tipo === tipo && plusOneDayISO(prev.fecha_fin) === r.fecha) {
            prev.fecha_fin = r.fecha;
            prev.dias++;
          } else {
            grupos.push({ fecha_inicio: r.fecha, fecha_fin: r.fecha, dias: 1, tipo });
          }
        }
        if (mounted) setRangos(grupos.reverse());
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [empleadoId]);

  const totalDisfrutadas = rangos.filter(g => g.tipo === "Disfrutadas").reduce((s, g) => s + g.dias, 0);
  const totalPagadas = rangos.filter(g => g.tipo === "Pagadas").reduce((s, g) => s + g.dias, 0);

  return (
    <div data-testid="expediente-historial-vacaciones">
      <div className="text-xs uppercase tracking-widest text-secondary-c font-medium mb-3 flex items-center gap-2">
        <Palmtree size={14} className="text-primary-color" />
        Historial de vacaciones
        {!loading && rangos.length > 0 && (
          <span className="text-secondary-c font-normal normal-case tracking-normal">
            · {totalDisfrutadas} disfrutadas · {totalPagadas} pagadas
          </span>
        )}
      </div>
      {loading ? (
        <div className="text-sm text-secondary-c">Cargando…</div>
      ) : rangos.length === 0 ? (
        <div className="p-4 rounded-xl bg-[#FAFAF7] border border-dashed border-c text-sm text-secondary-c">
          Sin vacaciones registradas.
        </div>
      ) : (
        <div className="rounded-xl border border-c overflow-hidden">
          <table className="np-table text-sm">
            <thead><tr>
              <th>Desde</th>
              <th>Hasta</th>
              <th className="text-center">Días</th>
              <th className="text-right">Monto pagado</th>
              <th className="text-center">Tipo</th>
            </tr></thead>
            <tbody>
              {rangos.map(g => (
                <tr key={`${g.fecha_inicio}-${g.tipo}-${g.dias}`}>
                  <td>{fmtFecha(g.fecha_inicio)}{g.tipo === "Pagadas" && <span className="text-secondary-c text-xs ml-1">(pago)</span>}</td>
                  <td>{g.tipo === "Pagadas" ? <span className="text-secondary-c">—</span> : fmtFecha(g.fecha_fin)}</td>
                  <td className="text-center font-medium">{g.dias}</td>
                  <td className="text-right">{g.tipo === "Pagadas" && g.monto_pagado > 0 ? colones(g.monto_pagado) : <span className="text-secondary-c">—</span>}</td>
                  <td className="text-center">
                    <span className={`np-badge ${g.tipo === "Pagadas" ? "np-badge-warning" : "np-badge-success"}`}>{g.tipo}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function plusOneDayISO(iso) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function VacacionesDisponibles({ empleado, empresa }) {
  const [saldo, setSaldo] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/asistencia");
        const { saldoTotal } = calcularPeriodosDetallados(empleado, data || [], new Date(), empresa);
        if (alive) setSaldo(saldoTotal);
      } catch { if (alive) setSaldo(null); }
    })();
    return () => { alive = false; };
  }, [empleado?.id, empresa]);
  const val = saldo != null ? `${saldo} ${saldo === 1 ? "día" : "días"}` : "—";
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-secondary-c font-medium mb-1">Vacaciones disponibles</div>
      <div className="text-sm font-medium text-primary-c">{val}</div>
    </div>
  );
}

